<template>
  <div>
    <div class="otp-input-container">
      <div class="otp-boxes" dir="ltr">        
        <div 
          v-for="index in Number(otpLength)" 
          :key="index" 
          class="otp-input-group"
        >
          <input
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            :ref="el => inputRefs[index - 1] = el"
            v-model="otpDigits[index - 1]"
            maxlength="1"
            class="otp-box"
            @input="handleInput(index - 1)"
            @keydown="handleKeydown($event, index - 1)"
            @paste="handlePaste"
            @focus="$event.target.select()"
          />
          <span v-if="index < otpLength" class="otp-separator"></span>
        </div>
      </div>
    </div>

    <!-- Add resend button section -->
    <div class="resend-container mt-4 text-center">
      <button
        v-if="countdown > 0"
        class="resend-otp-button text-base rounded-full text-[#0D0D0D] bg-[#F2F2F2] text-center w-full mt-0 focus:outline-none ring-0 py-3 px-6"
        disabled
      >
      {{ countdown }} {{ $t('resendIn') }}
      </button>
      <button
        v-else
        class="resend-otp-button text-base rounded-full text-[#0D0D0D] bg-[#F2F2F2] text-center w-full mt-0 focus:outline-none ring-0 py-3 px-6"
        @click="$emit('resend')"
      >
        {{ $t('resendOtp') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  resendDelay: {
    type: Number,
    default: 60
  },
  otpLength: {
    type: Number,
    default: 5
  }
})

const emit = defineEmits(['update:modelValue', 'resend', 'complete'])

const otpDigits = ref(Array(Number(props.otpLength)).fill(''))
const inputRefs = ref([])
const countdown = ref(props.resendDelay)
let timer = null

const startCountdown = () => {
  countdown.value = props.resendDelay
  clearInterval(timer)
  timer = setInterval(() => {
    if (countdown.value > 0) {
      countdown.value--
    } else {
      clearInterval(timer)
    }
  }, 1000)
}

const handleInput = (index) => {
  const persianToEnglish = (str) => {
    const persianNumbers = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']
    const arabicNumbers = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩']
    
    return str.split('').map(c => {
      const persianIndex = persianNumbers.indexOf(c)
      if (persianIndex >= 0) return persianIndex.toString()
      
      const arabicIndex = arabicNumbers.indexOf(c)
      if (arabicIndex >= 0) return arabicIndex.toString()
      
      return c
    }).join('')
  }

  otpDigits.value[index] = persianToEnglish(otpDigits.value[index]).replace(/[^0-9]/g, '')
  
  if (otpDigits.value[index] && index < props.otpLength - 1) {
    inputRefs.value[index + 1]?.focus()
  }
  
  updateModelValue()
  
  // Check if all digits are filled and automatically trigger verification
  const isComplete = otpDigits.value.every(digit => digit !== '');
  if (isComplete) {
    // Use setTimeout to ensure the model value is updated before emitting the complete event
    setTimeout(() => {
      emit('complete')
    }, 100)
  }
}

const handleKeydown = (event, index) => {
  if (event.key === 'Backspace') {
    if (!otpDigits.value[index] && index > 0) {
      otpDigits.value[index - 1] = ''
      inputRefs.value[index - 1]?.focus()
      event.preventDefault()
    }
  }
  else if (event.key === 'ArrowLeft' && index > 0) {
    inputRefs.value[index - 1]?.focus()
  }
  else if (event.key === 'ArrowRight' && index < props.otpLength - 1) {
    inputRefs.value[index + 1]?.focus()
  }
}

const handlePaste = (event) => {
  event.preventDefault()
  const pastedData = event.clipboardData.getData('text')
  const numbers = pastedData.replace(/[^0-9]/g, '').slice(0, props.otpLength)
  
  numbers.split('').forEach((num, index) => {
    if (index < props.otpLength) {
      otpDigits.value[index] = num
    }
  })
  
  updateModelValue()
  
  const lastIndex = Math.min(numbers.length, props.otpLength - 1)
  inputRefs.value[lastIndex]?.focus()
}

const updateModelValue = () => {
  emit('update:modelValue', otpDigits.value.join(''))
}

watch(() => props.modelValue, (newValue) => {
  const digits = newValue.replace(/[^0-9]/g, '').slice(0, props.otpLength).split('')
  otpDigits.value = [...digits, ...Array(props.otpLength - digits.length).fill('')]
})

onMounted(() => {
  inputRefs.value[0]?.focus()
  startCountdown()
})

onUnmounted(() => {
  clearInterval(timer)
})

watch(() => props.resendDelay, (newDelay) => {
  countdown.value = newDelay
})

watch(() => props.otpLength, (newLength) => {
  otpDigits.value = Array(newLength).fill('')
  inputRefs.value = []
})

defineExpose({
  startCountdown
})
</script>

<style scoped>
.otp-input-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 1rem 0;
}

.otp-boxes {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  direction: ltr !important;
}

.otp-box {
  width: 3rem;
  height: 3.5rem;
  border: 2px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 1.5rem;
  text-align: center;
  transition: all 0.3s ease;
  background: white;
  direction: ltr !important;
}
 
.otp-box:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
  outline: none;
}

.otp-separator {
  width: 8px;
  height: 2px;
  background-color: #d1d5db;
  margin: 0 4px;
}

/* Remove spinner buttons from number input */
.otp-box::-webkit-outer-spin-button,
.otp-box::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.otp-box[type=number] {
  -moz-appearance: textfield;
}

/* Force LTR for all browsers */
input.otp-box {
  direction: ltr !important;
  text-align: center !important;
  unicode-bidi: bidi-override;
}

.resend-container {
  margin-top: 1rem;
  text-align: center;
}

.resend-button {
  cursor: pointer;
  transition: all 0.3s ease;
}

.resend-button:disabled {
  cursor: not-allowed;
}

.resend-button:not(:disabled):hover {
  color: #4f46e5;
}
@media screen and (max-width: 768px) {
    .otp-box {
      width: 2.5rem;
      height: 2.5rem;
      font-size: 1.25rem;
    }
    
  }
</style> 